import { GameStatues, IGame, IGameAttendances } from "@grassrootly/api";
import { PlayerStats } from "../../../screens/edit-game/useEditGame";
import { useEffect, useMemo, useRef, useState } from "react";
import { GameTeams } from "../lib";
import { GameType } from "../../../screens/new-game/NewGame.types";

export type EditGameStatsProps = {
  attendees: IGameAttendances[];
  updateDisabled: boolean;
  disableScoreEdit?: boolean; // Separate control for score editing (when game is FINISHED)
  setPlayerStats: (params: PlayerStats[]) => void;
  updateGameScore: (params: { hostGoals: number; visitorGoals: number }) => void;
  goals: Array<{ team: string; goals: number }>;
  gameStatus: GameStatues;
  game: IGame;
};

export type TeamStat = {
  teamKey: string;
  teamLabel: string;
  goals: number;
  assists: number;
  selected: boolean;
  lineup: IGameAttendances[];
};

export interface IEditGameStatsHook {
  updatePlayerStats: (gamePlayer: IGameAttendances, goals?: number, assists?: number, ownGoals?: number, errors?: number, yellowCards?: number, redCards?: number, saves?: number) => void;
  unsavedChanges: IGameAttendances[];
  clearChanges: () => void;
  saveChanges: () => Promise<void>;
  teamsStats: TeamStat[];
  selectTeam: (key: string) => void;
  getSelectedTeam: () => TeamStat[];
  setTeamGoals: (goals: number, teamKey: string) => void;
}

const makeEmptyTeams = (): TeamStat[] => [
  {
    teamKey: GameTeams.hosts.key,
    teamLabel: GameTeams.hosts.label,
    goals: 0,
    assists: 0,
    selected: true,
    lineup: [],
  },
  {
    teamKey: GameTeams.visitors.key,
    teamLabel: GameTeams.visitors.label,
    goals: 0,
    assists: 0,
    selected: false,
    lineup: [],
  },
];

export function useEditGameStats({
  attendees,
  setPlayerStats,
  updateGameScore,
  goals,
  game,
}: EditGameStatsProps): IEditGameStatsHook {
  const [unsavedChanges, setUnsavedChanges] = useState<IGameAttendances[]>([]);
  const [teamsStats, setTeamsStats] = useState<TeamStat[]>(() => makeEmptyTeams());

  // Track if user has locally edited goals/assists; used to prevent rebuilds from props
  const isDirtyRef = useRef(false);

  // Compute goals map from prop (server) values
  const goalsMap = useMemo(() => {
    const map = new Map<string, number>();
    goals?.forEach((g) => map.set(g.team, g.goals));
    return map;
  }, [goals]);

  /** Rebuild team stats from server props
   * IMPORTANT: Do NOT clobber local edits if there are unsaved changes.
   */
  useEffect(() => {
    if (unsavedChanges.length > 0 || isDirtyRef.current) return;

    const base = makeEmptyTeams();

    if (game.game_type === GameType.COMPETITIVE) {
      const isHostTeam = game?.host_team_name === game.group?.name;
      const targetKey = isHostTeam ? base[0].teamKey : base[1].teamKey;

      const lineup = attendees.filter((a) => a.attended).map((a) => ({ ...a, team: targetKey }));

      const idx = base.findIndex((t) => t.teamKey === targetKey);
      if (idx !== -1) {
        base[idx] = {
          ...base[idx],
          lineup,
          goals: goalsMap.get(targetKey) ?? 0,
          assists: 0,
        };
      }

      const otherIdx = idx === 0 ? 1 : 0;
      base[otherIdx] = {
        ...base[otherIdx],
        lineup: [],
        goals: goalsMap.get(base[otherIdx].teamKey) ?? 0,
        assists: 0,
      };
    } else {
      // TRAINING
      for (let i = 0; i < base.length; i++) {
        const t = base[i];
        const lineup = attendees.filter((a) => a.attended && a.team === t.teamKey);
        base[i] = {
          ...t,
          lineup,
          goals: goalsMap.get(t.teamKey) ?? 0,
          assists: 0,
        };
      }
    }

    setTeamsStats(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendees, goalsMap, game.game_type, game?.host_team_name, game.group?.name, unsavedChanges.length]);

  // Local helpers

  const updatePlayerStats = (gamePlayer: IGameAttendances, goalsDelta = 0, assistsDelta = 0, ownGoalsDelta = 0, errorsDelta = 0, yellowCardsDelta = 0, redCardsDelta = 0, savesDelta = 0) => {
    isDirtyRef.current = true;

    setTeamsStats((prev) =>
      prev.map((team) => {
        if (team.teamKey !== gamePlayer.team) return team;
        const newLineup = team.lineup.map((p) => {
          if (p.player_id !== gamePlayer.player_id) return p;
          const nextAssists = Math.max(0, (p.assists_made ?? 0) + assistsDelta);
          const nextGoals = Math.max(0, (p.goals_scored ?? 0) + goalsDelta);
          const nextOwnGoals = Math.max(0, (p.own_goals ?? 0) + ownGoalsDelta);
          const nextErrors = Math.max(0, (p.errors_leading_to_a_goal ?? 0) + errorsDelta);
          const nextYellowCards = Math.max(0, (p.yellow_cards ?? 0) + yellowCardsDelta);
          const nextRedCards = Math.max(0, (p.red_cards ?? 0) + redCardsDelta);
          const nextSaves = Math.max(0, (p.saves ?? 0) + savesDelta);
          return { 
            ...p, 
            assists_made: nextAssists, 
            goals_scored: nextGoals, 
            own_goals: nextOwnGoals, 
            errors_leading_to_a_goal: nextErrors,
            yellow_cards: nextYellowCards,
            red_cards: nextRedCards,
            saves: nextSaves
          };
        });
        return { ...team, lineup: newLineup };
      })
    );

    setUnsavedChanges((prev) => {
      const existingIdx = prev.findIndex((x) => x.player_id === gamePlayer.player_id);
      const updated: IGameAttendances = {
        ...gamePlayer,
        assists_made: Math.max(0, (gamePlayer.assists_made ?? 0) + assistsDelta),
        goals_scored: Math.max(0, (gamePlayer.goals_scored ?? 0) + goalsDelta),
        own_goals: Math.max(0, (gamePlayer.own_goals ?? 0) + ownGoalsDelta),
        errors_leading_to_a_goal: Math.max(0, (gamePlayer.errors_leading_to_a_goal ?? 0) + errorsDelta),
        yellow_cards: Math.max(0, (gamePlayer.yellow_cards ?? 0) + yellowCardsDelta),
        red_cards: Math.max(0, (gamePlayer.red_cards ?? 0) + redCardsDelta),
        saves: Math.max(0, (gamePlayer.saves ?? 0) + savesDelta),
      };
      if (existingIdx === -1) return [...prev, updated];
      const copy = [...prev];
      copy[existingIdx] = updated;
      return copy;
    });
  };

  const clearChanges = () => {
    isDirtyRef.current = false;
    setUnsavedChanges([]);
  };

  const saveChanges = async () => {
    // Persist players' stats first
    const playerStats: PlayerStats[] = unsavedChanges.map((x) => ({
      playerId: x.player_id,
      goals: x.goals_scored ?? 0,
      assists: x.assists_made ?? 0,
      ownGoals: x.own_goals ?? 0,
      errors: x.errors_leading_to_a_goal ?? 0,
      yellowCards: x.yellow_cards ?? 0,
      redCards: x.red_cards ?? 0,
      saves: x.saves ?? 0,
    }));

    await Promise.resolve(setPlayerStats(playerStats));

    // Then persist the team goals currently visible in the UI (optimistic in useEditGame)
    await Promise.resolve(
      updateGameScore({
        hostGoals: teamsStats[0]?.goals ?? 0,
        visitorGoals: teamsStats[1]?.goals ?? 0,
      })
    );

    clearChanges();
  };

  const selectTeam = (key: string) => {
    setTeamsStats((prev) => prev.map((t) => ({ ...t, selected: t.teamKey === key })));
  };

  const getSelectedTeam = () => {
    return game.game_type === GameType.TRAINING ? teamsStats.filter((t) => t.selected) : teamsStats;
  };

  const setTeamGoals = (value: number, teamKey: string) => {
    if (!teamKey) return;
    isDirtyRef.current = true;

    setTeamsStats((prev) => prev.map((t) => (t.teamKey === teamKey ? { ...t, goals: Math.max(0, value || 0) } : t)));

    // Ensure "unsaved changes" bar appears even if only team goals changed
    setUnsavedChanges((prev) => {
      if (prev.length > 0) return prev;
      const firstAttendee = attendees?.[0];
      return firstAttendee ? [firstAttendee] : prev;
    });
  };

  return {
    updatePlayerStats,
    unsavedChanges,
    clearChanges,
    saveChanges,
    teamsStats,
    selectTeam,
    getSelectedTeam,
    setTeamGoals,
  };
}
